import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

// Helper to get API URL - uses current origin in production
const getApiUrl = () => {
  if (typeof window !== 'undefined' && window.location.origin.includes('xperience-guides.com')) {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || window.location.origin;
};
const API_URL = getApiUrl();

const ForgotPasswordPage = () => {
  const { i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      setSent(true);
      toast.success(i18n.language === 'fr' 
        ? 'Email envoyé ! Vérifiez votre boîte de réception.' 
        : 'Email sent! Check your inbox.');
    } catch (error) {
      // Don't reveal if email exists or not for security
      setSent(true);
      toast.success(i18n.language === 'fr' 
        ? 'Si cet email existe, vous recevrez un lien de réinitialisation.' 
        : 'If this email exists, you will receive a reset link.');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <main className="min-h-screen pt-20 pb-12 flex items-center" data-testid="forgot-password-success">
        <div className="max-w-md mx-auto px-4 text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <CheckCircle className="w-10 h-10 text-green-600" />
          </motion.div>
          <h1 className="text-2xl font-bold mb-4">
            {i18n.language === 'fr' ? 'Email envoyé !' : 'Email sent!'}
          </h1>
          <p className="text-muted-foreground mb-8">
            {i18n.language === 'fr' 
              ? 'Si un compte existe avec cette adresse email, vous recevrez un lien pour réinitialiser votre mot de passe.' 
              : 'If an account exists with this email address, you will receive a link to reset your password.'}
          </p>
          <Link to="/login">
            <Button variant="outline" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              {i18n.language === 'fr' ? 'Retour à la connexion' : 'Back to login'}
            </Button>
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen pt-20 pb-12 flex items-center" data-testid="forgot-password-page">
      <div className="max-w-md mx-auto px-4 w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Link to="/login" className="inline-flex items-center text-muted-foreground hover:text-foreground mb-8">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {i18n.language === 'fr' ? 'Retour' : 'Back'}
          </Link>

          <h1 className="text-3xl font-bold mb-2">
            {i18n.language === 'fr' ? 'Mot de passe oublié ?' : 'Forgot password?'}
          </h1>
          <p className="text-muted-foreground mb-8">
            {i18n.language === 'fr' 
              ? 'Entrez votre email et nous vous enverrons un lien pour réinitialiser votre mot de passe.' 
              : 'Enter your email and we\'ll send you a link to reset your password.'}
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  placeholder="vous@exemple.com"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-primary hover:bg-primary/90 py-6"
              disabled={loading}
              data-testid="submit-btn"
            >
              {loading 
                ? (i18n.language === 'fr' ? 'Envoi en cours...' : 'Sending...') 
                : (i18n.language === 'fr' ? 'Envoyer le lien' : 'Send reset link')}
            </Button>
          </form>

          <p className="text-center mt-6 text-muted-foreground">
            {i18n.language === 'fr' ? 'Vous vous souvenez ?' : 'Remember it?'}{' '}
            <Link to="/login" className="text-primary font-medium hover:underline">
              {i18n.language === 'fr' ? 'Se connecter' : 'Log in'}
            </Link>
          </p>
        </motion.div>
      </div>
    </main>
  );
};

export default ForgotPasswordPage;
